/**
 * The icon set.
 *
 * Drawn by hand on a 24 grid, 1.8 stroke, round joins, currentColor. No icon
 * library is used anywhere in the product - these are all original shapes.
 */

export type IconProps = { size?: number; className?: string };

function S({ size = 16, className, children }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
      focusable="false"
    >
      {children}
    </svg>
  );
}

/* ---- scope: everyone / friends / invited / mine ---- */
export const Globe = (p: IconProps) => (
  <S {...p}><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18" /></S>
);
export const Users = (p: IconProps) => (
  <S {...p}><circle cx="9" cy="8.2" r="3.3" /><path d="M2.8 20.3c0-3.3 2.8-5.6 6.2-5.6s6.2 2.3 6.2 5.6M16.4 5.4a3.2 3.2 0 0 1 0 6M18 14.9c2.1.6 3.6 2.4 3.6 4.8" /></S>
);
export const Inbox = (p: IconProps) => (
  <S {...p}><path d="M3.2 13.4 5.4 5a2 2 0 0 1 1.9-1.4h9.4A2 2 0 0 1 18.6 5l2.2 8.4v4.6a2 2 0 0 1-2 2H5.2a2 2 0 0 1-2-2Z" /><path d="M3.2 13.4h4.4l1.2 2.4h6.4l1.2-2.4h4.4" /></S>
);
export const Star = (p: IconProps) => (
  <S {...p}><path d="M12 2.8 14.7 8l5.7.8-4.1 4 1 5.7-5.3-2.8-5.3 2.8 1-5.7-4.1-4L9.3 8Z" /></S>
);

/* ---- friends tabs ---- */
export const Grid = (p: IconProps) => (
  <S {...p}><rect x="3.2" y="3.2" width="7.4" height="7.4" rx="2" /><rect x="13.4" y="3.2" width="7.4" height="7.4" rx="2" /><rect x="3.2" y="13.4" width="7.4" height="7.4" rx="2" /><rect x="13.4" y="13.4" width="7.4" height="7.4" rx="2" /></S>
);
export const CheckCircle = (p: IconProps) => (
  <S {...p}><circle cx="12" cy="12" r="9" /><path d="m8.2 12.3 2.6 2.6 5-5" /></S>
);
export const Ban = (p: IconProps) => (
  <S {...p}><circle cx="12" cy="12" r="9" /><path d="m5.6 5.6 12.8 12.8" /></S>
);

/* ---- staff tabs ---- */
export const Chart = (p: IconProps) => (
  <S {...p}><path d="M3.5 20.5h17M6.5 20.5v-6M11 20.5V7.5M15.5 20.5v-9M20 20.5V4.5" /></S>
);
export const Scroll = (p: IconProps) => (
  <S {...p}><path d="M6.4 3.5h11.2a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H6.4a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2Z" /><path d="M8.2 8h7.6M8.2 12h7.6M8.2 16h4.4" /></S>
);
export const Cube = (p: IconProps) => (
  <S {...p}><path d="M12 2.9 20 7v9.9L12 21l-8-4.1V7Z" /><path d="M12 11.6 20 7M12 11.6 4 7m8 4.6V21" /></S>
);

/* ---- settings tabs ---- */
export const Profile = (p: IconProps) => (
  <S {...p}><circle cx="12" cy="8" r="3.6" /><path d="M4.6 20.6c.5-4 3.6-6.2 7.4-6.2s6.9 2.2 7.4 6.2" /></S>
);
export const Palette = (p: IconProps) => (
  <S {...p}><path d="M12 3.2a8.8 8.8 0 0 0 0 17.6c1.2 0 1.9-.8 1.9-1.8 0-1.3-1-1.8-1-2.8 0-.9.7-1.6 1.7-1.6h2A5.2 5.2 0 0 0 21 9.4c-.6-3.6-4.4-6.2-9-6.2Z" /><circle cx="7.8" cy="11.4" r="1.1" fill="currentColor" stroke="none" /><circle cx="10.4" cy="7.4" r="1.1" fill="currentColor" stroke="none" /><circle cx="15.2" cy="7.8" r="1.1" fill="currentColor" stroke="none" /></S>
);
export const Headset = (p: IconProps) => (
  <S {...p}><path d="M4 12v-2a8 8 0 0 1 16 0v2M4 14a2 2 0 0 1 2-2h1v6H6a2 2 0 0 1-2-2v-2ZM20 14a2 2 0 0 0-2-2h-1v6h1a2 2 0 0 0 2-2v-2Z" /></S>
);
export const Lock = (p: IconProps) => (
  <S {...p}><rect x="4.4" y="10.2" width="15.2" height="10.4" rx="2.2" /><path d="M8 10.2V7.4a4 4 0 0 1 8 0v2.8" /></S>
);
export const Plug = (p: IconProps) => (
  <S {...p}><circle cx="12" cy="12" r="9" /><path d="M3.6 9.4h16.8M3.6 14.6h16.8M12 3a16 16 0 0 1 0 18 16 16 0 0 1 0-18" /></S>
);
export const Key = (p: IconProps) => (
  <S {...p}><circle cx="8.4" cy="15.6" r="4.2" /><path d="m11.4 12.6 8.2-8.2M17 7l2.2 2.2M14.6 9.4l2.2 2.2" /></S>
);

/* ---- general ---- */
export const Plus = (p: IconProps) => <S {...p}><path d="M12 5v14M5 12h14" /></S>;
export const Search = (p: IconProps) => <S {...p}><circle cx="10.7" cy="10.7" r="6.4" /><path d="m15.6 15.6 4.6 4.6" /></S>;
export const Close = (p: IconProps) => <S {...p}><path d="M6 6 18 18M18 6 6 18" /></S>;
export const ArrowLeft = (p: IconProps) => <S {...p}><path d="M20 12H5M11 18l-6-6 6-6" /></S>;
export const Bolt = (p: IconProps) => <S {...p}><path d="M13.6 2.6 5 13.4h5.6L9.8 21.4l8.8-11h-5.8Z" /></S>;
export const Crown = (p: IconProps) => <S {...p}><path d="M3.4 7.6 7 12l5-6.6L17 12l3.6-4.4-1.4 11H4.8Z" /><path d="M4.8 20.4h14.4" /></S>;
/*
   The mark beside a staff name.

   A scalloped rosette at 22px turned to mush - too many little points to
   resolve, and it read as a smudge rather than a mark. This is a plain disc
   with a tick knocked out of it: one silhouette, legible at any size, and the
   tick is cut from the surface colour so it stays crisp on any background.
*/
export const Verified = ({ size = 16, className }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden focusable="false">
    <circle cx="12" cy="12" r="10" fill="currentColor" />
    <path
      d="m7.8 12.3 2.9 2.9 5.5-5.9"
      fill="none"
      stroke="var(--bg-1)"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const Shield = (p: IconProps) => <S {...p}><path d="M12 2.9 20 6v6.2c0 4.3-3.2 7.6-8 9-4.8-1.4-8-4.7-8-9V6Z" /><path d="m8.8 11.9 2.2 2.2 4.2-4.4" /></S>;
export const Smile = (p: IconProps) => (
  <S {...p}><circle cx="12" cy="12" r="9" /><path d="M8.4 14.2a4.4 4.4 0 0 0 7.2 0" /><circle cx="9" cy="9.6" r="1" fill="currentColor" stroke="none" /><circle cx="15" cy="9.6" r="1" fill="currentColor" stroke="none" /></S>
);
export const Reply = (p: IconProps) => <S {...p}><path d="M9 7 4 12l5 5" /><path d="M4 12h9a6 6 0 0 1 6 6v1" /></S>;
export const Edit = (p: IconProps) => <S {...p}><path d="M4 20h4l10-10-4-4L4 16v4ZM14.5 5.5l4 4" /></S>;
export const Trash = (p: IconProps) => <S {...p}><path d="M5 7h14M9 7V5h6v2M7 7l1 13h8l1-13" /></S>;
export const Mic = (p: IconProps) => <S {...p}><rect x="9" y="2.6" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></S>;
export const Upload = (p: IconProps) => <S {...p}><path d="M12 16V4.6M7.6 9 12 4.6 16.4 9" /><path d="M4.4 15.4v3.4a1.8 1.8 0 0 0 1.8 1.8h11.6a1.8 1.8 0 0 0 1.8-1.8v-3.4" /></S>;
export const Send = (p: IconProps) => <S {...p}><path d="M4 12 20 4l-4 16-4-7-8-1Z" /></S>;
export const Command = (p: IconProps) => (
  <S {...p}><path d="M8.4 3.6a2.4 2.4 0 1 0 0 4.8h7.2a2.4 2.4 0 1 0 0-4.8v0a2.4 2.4 0 0 0-2.4 2.4v12a2.4 2.4 0 1 1-2.4 2.4v0a2.4 2.4 0 0 1 2.4-2.4h7.2a2.4 2.4 0 1 1-2.4-2.4H8.4a2.4 2.4 0 1 1 2.4-2.4V6a2.4 2.4 0 0 0-2.4-2.4Z" /></S>
);
export const Bell = (p: IconProps) => <S {...p}><path d="M18 8.6a6 6 0 1 0-12 0c0 5.2-2.2 6.8-2.2 6.8h16.4S18 13.8 18 8.6Z" /><path d="M13.7 19.4a2 2 0 0 1-3.4 0" /></S>;
export const Type = (p: IconProps) => <S {...p}><path d="M4.4 7V4.6h15.2V7M12 4.6v14.8M8.4 19.4h7.2" /></S>;
export const Sliders = (p: IconProps) => <S {...p}><path d="M4 7h10M18 7h2M4 17h4M12 17h8" /><circle cx="16" cy="7" r="2.2" /><circle cx="10" cy="17" r="2.2" /></S>;
